/* eslint-disable react/no-multi-comp */

import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import {styled} from '@mui/material/styles';
import {FC, Fragment, useRef, useState} from 'react';
import {Box} from '../Box';
import {IconButton} from '../Button';
import {Drawer, DrawerProps as DProps} from '../Drawer';
import {List} from '../List';
import {Typography} from '../Typography';
import {NavItem} from './NavItem';
import {NavItemButton} from './NavItemButton';
import {NavigationLinkOptions} from './types';

export interface NavDrawerProps extends DProps {
    items?: NavigationLinkOptions[];
    footerItems?: NavigationLinkOptions[];
}

export interface NavItemDrawerProps extends NavigationLinkOptions {
    onClose?: DProps['onClose'];
}

const StyledMenuHeader = styled('div')(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.tokens.FuelSpacing2,
    paddingBottom: theme.tokens.FuelSpacing2,
    paddingLeft: theme.tokens.FuelSpacing4,
    paddingRight: '3.25rem', // iconSize + padding(1rem)
    borderBottom: `solid 1px ${theme.tokens.FuelColorBorderLight}`,
    textAlign: 'center',

    '& .FuelStyledMenuHeader-text': {
        paddingLeft: theme.tokens.FuelSpacing2,
        paddingRight: theme.tokens.FuelSpacing2
    }
}));

const StyledNav = styled(List)({
    display: 'block',
    flexGrow: 1,
    boxShadow: 'none',
    width: '100%',
    minWidth: '15rem',
    paddingLeft: 0,
    paddingRight: 0
});

const StyledList = styled(List)(({theme}) => ({
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0,
    width: '100%',
    paddingLeft: theme.tokens.FuelSpacing1,
    paddingRight: theme.tokens.FuelSpacing1,

    '& .MuiListItem-root:not(:first-of-type)': {
        marginTop: theme.tokens.FuelSpacing1
    },

    '&.MuiList-root': {
        margin: 0
    }
}));

const StyledNavItemButton = styled(NavItemButton)(({theme}) => ({
    '&.MuiButton-root': {
        fontSize: theme.tokens.FuelFontSizeBase,
        padding: `${theme.tokens.FuelSpacing2} ${theme.tokens.FuelSpacing4}`,
        height: theme.tokens.FuelSpacing12,

        '& .FuelNavItemButton-text': {
            lineHeight: theme.tokens.FuelLineHeightBase,
            paddingRight: theme.tokens.FuelSpacing4
        }
    }
}));

const StyledDrawer = styled(Drawer)(({theme}) => ({
    '& .MuiPaper-root': {
        width: '100%',
        maxWidth: theme.breakpoints.values.xs,

        [theme.breakpoints.down('xs')]: {
            maxWidth: 'auto'
        }
    }
}));

const NavItemDrawer: FC<NavItemDrawerProps> = ({
    label, selected, subNavigationItems: items, onClose, ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <Fragment>
            <StyledNavItemButton
                endIcon={<KeyboardArrowRight/>}
                onClick={(): void => {
                    setIsOpen(true);
                }}
                orientation='side'
                selected={selected}
                {...props}
            >
                {label}
            </StyledNavItemButton>
            <StyledDrawer
                BackdropProps={{
                    invisible: true
                }}
                elevation={0}
                hideBackdrop
                open={isOpen}
                position='left'
                variant='persistent'
            >
                <Box
                    display='flex'
                    flexDirection='column'
                >
                    <StyledMenuHeader>
                        <IconButton
                            onClick={(): void => {
                                setIsOpen(false);
                            }}
                        >
                            <KeyboardArrowLeft/>
                        </IconButton>
                        <Typography
                            className='FuelStyledMenuHeader-text'
                            color='secondary'
                            component='span'
                            flexGrow={1}
                            noWrap
                            textAlign='center'
                            variant='h4'
                        >
                            {label}
                        </Typography>
                    </StyledMenuHeader>
                    <StyledNav component='nav'>
                        <StyledList>
                            {items && items.map((item, itemIndex) => {
                                if (item.subNavigationItems && item.subNavigationItems.length > 0) {
                                    return (
                                        <NavItem key={`${item.label }-${ itemIndex}`}>
                                            <NavItemDrawer
                                                onClose={onClose}
                                                {...item}
                                            />
                                        </NavItem>
                                    );
                                }

                                return (
                                    <NavItem key={`${item.label }-${ itemIndex}`}>
                                        <StyledNavItemButton
                                            {...item}
                                            onClick={(clickEvent): void => {
                                                if (item.onClick) {
                                                    item.onClick(clickEvent);
                                                }

                                                if (onClose) {
                                                    onClose(clickEvent, 'backdropClick');
                                                }
                                            }}
                                            orientation='side'
                                        >
                                            {item.label}
                                        </StyledNavItemButton>
                                    </NavItem>
                                );
                            })}
                        </StyledList>
                    </StyledNav>
                </Box>
            </StyledDrawer>
        </Fragment>
    );
};

export const NavDrawer: FC<NavDrawerProps> = ({
    onClose, open, items, ...props
}) => {
    const containerRef = useRef(null);

    return (
        <StyledDrawer
            {...props}
            onClose={onClose}
            open={open}
            position='left'
        >
            <ClickAwayListener
                onClickAway={(clickEvent): void => {
                    if (onClose) {
                        onClose(clickEvent, 'backdropClick');
                    }
                }}
            >
                <Box
                    display='flex'
                    flexDirection='column'
                    ref={containerRef}
                >
                    <StyledNav component='nav'>
                        <StyledList>
                            {items && items.map((item, itemIndex) => {
                                if (item.subNavigationItems && item.subNavigationItems.length > 0) {
                                    return (
                                        <NavItem key={`${item.label }-${ itemIndex}`}>
                                            <NavItemDrawer
                                                {...item}
                                                onClose={onClose}
                                            />
                                        </NavItem>
                                    );
                                }

                                return (
                                    <NavItem key={`${item.label }-${ itemIndex}`}>
                                        <StyledNavItemButton
                                            {...item}
                                            onClick={(clickEvent): void => {
                                                if (item.onClick) {
                                                    item.onClick(clickEvent);
                                                }

                                                if (onClose) {
                                                    onClose(clickEvent, 'backdropClick');
                                                }
                                            }}
                                            orientation='side'
                                            selected={item.selected}
                                        >
                                            {item.label}
                                        </StyledNavItemButton>
                                    </NavItem>
                                );
                            })}
                        </StyledList>
                    </StyledNav>
                </Box>
            </ClickAwayListener>
        </StyledDrawer>
    );
};
